import { cn } from "@oneleet/utils";
import {
  DEFAULT_LOADING_DELAY,
  useDelayedLoading,
} from "@oneleet/utils/src/hooks";

import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const loader = cva(
  "flex size-8 animate-spin items-center justify-center fill-current",
  {
    variants: {
      color: {
        accent: "text-accent-700",
        brand: "text-brand-400",
        gray: "text-gray-400",
      },
    },
  },
);

type LoaderVariantProps = VariantProps<typeof loader>;

export interface Props extends LoaderVariantProps {
  /** Shows the loader after a brief delay when true */
  delayed?: boolean;
  /** Delay in milliseconds to wait before showing the loader */
  delay?: number;
  className?: string;
  classNames?: {
    root?: string;
    svg?: string;
  };
  id?: string;
}

const Loader = ({
  color = "accent",
  delayed = false,
  delay,
  className,
  classNames,
  id,
}: Props) => {
  if (delayed && !delay) {
    delay = DEFAULT_LOADING_DELAY;
  }
  const showLoader = useDelayedLoading({
    isLoading: true,
    delay: delay ?? 0,
  });

  if (!showLoader) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex h-full items-center justify-center",
        className,
        classNames?.root,
      )}
      id={id}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 41 40"
        className={cn(loader({ color, className: classNames?.svg }))}
      >
        <path
          opacity="0.24"
          d="M40.5 20C40.5 31.0457 31.5457 40 20.5 40C9.4543 40 0.5 31.0457 0.5 20C0.5 8.9543 9.4543 0 20.5 0C31.5457 0 40.5 8.9543 40.5 20ZM6.19759 20C6.19759 27.899 12.601 34.3024 20.5 34.3024C28.399 34.3024 34.8024 27.899 34.8024 20C34.8024 12.101 28.399 5.69759 20.5 5.69759C12.601 5.69759 6.19759 12.101 6.19759 20Z"
        />
        <path d="M38.5 20C39.6046 20 40.5104 19.1024 40.4001 18.0033C40.2053 16.0618 39.727 14.1557 38.9776 12.3463C37.9725 9.91982 36.4993 7.71504 34.6421 5.85786C32.785 4.00069 30.5802 2.5275 28.1537 1.52241C26.3443 0.772963 24.4382 0.294717 22.4967 0.0999171C21.3976 -0.0103573 20.5 0.895431 20.5 2V3.6976C20.5 4.80217 21.3997 5.68325 22.4935 5.83721C23.6862 6.00509 24.8556 6.32333 25.9733 6.7863C27.7085 7.50506 29.2852 8.55857 30.6133 9.88667C31.9414 11.2148 32.9949 12.7915 33.7137 14.5267C34.1767 15.6444 34.4949 16.8138 34.6628 18.0065C34.8168 19.1003 35.6978 20 36.8024 20H38.5Z" />
      </svg>
    </div>
  );
};

export default Loader;
